import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Stack, TextField, Typography, Chip, Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import { displayPath, isSensitivePath } from './ivrExtensionFieldSchemas.js';

function formatWhen(iso) {
  if (!iso) return '';
  return formatHebrewDateTime(iso) || String(iso);
}

export default function IvrBackupsDialog({
  open,
  filterPath = '',
  canRestore = false,
  onClose,
  onRestored,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [restoringId, setRestoringId] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null); // backup item
  const [confirmPath, setConfirmPath] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: 100 };
      if (filterPath) params.path = filterPath;
      const { data } = await api.get('/ivr/backups', { params });
      setItems(data.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'שגיאה בטעינת גיבויים');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filterPath]);

  useEffect(() => {
    if (!open) return;
    setConfirmFor(null);
    setConfirmPath('');
    setMsg('');
    load();
  }, [open, load]);

  const startRestore = (item) => {
    setMsg('');
    setConfirmFor(item);
    setConfirmPath('');
  };

  const doRestore = async () => {
    if (!confirmFor) return;
    const path = confirmFor.path;
    if (isSensitivePath(path)) {
      const typed = confirmPath.trim().replace(/^\/+/, '');
      if (typed !== String(path).replace(/^\/+/, '')) {
        setError(`נתיב רגיש — הקלידו: ${displayPath(path)}`);
        return;
      }
    }
    setRestoringId(confirmFor.id);
    setError('');
    try {
      const body = {};
      if (isSensitivePath(path)) body.confirmPath = path;
      const { data } = await api.post(`/ivr/backups/${confirmFor.id}/restore`, body);
      setMsg(data.note || 'ההגדרות שוחזרו');
      setConfirmFor(null);
      await load();
      onRestored?.(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'שחזור נכשל');
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
        גיבויי שלוחות
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={load} disabled={loading} aria-label="רענון">
          <RefreshIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          הגיבוי שומר את
          {' '}
          <strong>הגדרות השלוחה</strong>
          {' '}
          (ext.ini). קבצי שמע לא נשמרים כאן — שחזור מחזיר הגדרות בלבד.
        </Alert>

        {filterPath && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            מסונן לנתיב: {displayPath(filterPath)}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {msg && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>
            {msg}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            אין גיבויים עדיין
          </Typography>
        ) : (
          <Stack spacing={1.25} sx={{ maxHeight: 420, overflow: 'auto' }}>
            {items.map((b) => (
              <Box
                key={b.id}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'var(--ck-surface, #fff)',
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={700} sx={{ direction: 'ltr', textAlign: 'start' }}>
                      {b.displayPath || displayPath(b.path)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatWhen(b.createdAt)}
                      {b.createdBy ? ` · ${b.createdBy}` : ''}
                      {b.title ? ` · ${b.title}` : ''}
                    </Typography>
                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                      {b.type && <Chip size="small" label={b.type} />}
                      {b.restoredAt && (
                        <Chip size="small" color="success" label={`שוחזר ${formatWhen(b.restoredAt)}`} />
                      )}
                      {b.fileCount > 0 && (
                        <Chip size="small" variant="outlined" label={`${b.fileCount} קבצים במידע`} />
                      )}
                    </Stack>
                  </Box>
                  {canRestore && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RestoreIcon />}
                      onClick={() => startRestore(b)}
                      disabled={!!restoringId}
                    >
                      שחזר הגדרות
                    </Button>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}

        {confirmFor && (
          <>
            <Divider sx={{ my: 2 }} />
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              שחזור הגדרות לנתיב
              {' '}
              <strong style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                {confirmFor.displayPath || displayPath(confirmFor.path)}
              </strong>
              . אם השלוחה לא קיימת — תיווצר מחדש עם ההגדרות השמורות.
            </Alert>
            {isSensitivePath(confirmFor.path) && (
              <TextField
                label={`הקלידו לאישור: ${displayPath(confirmFor.path)}`}
                value={confirmPath}
                onChange={(e) => setConfirmPath(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 1.5 }}
                inputProps={{ dir: 'ltr' }}
              />
            )}
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setConfirmFor(null)} disabled={!!restoringId}>ביטול</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={doRestore}
                disabled={!!restoringId}
                startIcon={restoringId ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
              >
                אשר שחזור
              </Button>
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>סגור</Button>
      </DialogActions>
    </Dialog>
  );
}
